import React from 'react';
import { MAX_ELEMENT_DEPTH, type ElementNodeDto } from '@xshop/shared';
import { isVoidElement } from '../elementCatalog';
import { ElementEmptySlot } from './ElementEmptySlot';
import { useElementRender } from './elementRenderContext';
import { ELEMENT_RENDERERS } from './elementRenderers';
import { useBoundNode } from './useBoundNode';

// The walk. One node, one lookup, one component, then the same again for each child.
//
// `readElementTree` already bounds a stored tree to depth 12 and 2000 nodes, but the builder holds a
// tree in memory that has not been through it — a drag that reparents a node into its own subtree
// makes a cycle out of a tree that was fine a moment ago. So the walk carries the ids of its own
// ancestors and refuses to draw one twice, rather than recursing until the tab dies.

/** One node and the ids above it, which is what makes a cycle visible without walking the whole tree. */
type ElementNodeViewProps = { node: ElementNodeDto; path: readonly string[] };

/** The path a root starts from, hoisted so every root is not given a fresh array on every render. */
export const ROOT_PATH: readonly string[] = [];

/**
 * Draws a node's children, adding the node itself to the path so a child cannot draw its own parent.
 *
 * They arrive as a *list*, not a fragment. An element that has to treat each child on its own — a
 * carousel, whose children are its slides — reads them with `React.Children`, and a fragment counts as
 * one child however many it holds: the slideshow put all three slides inside slide one.
 *
 * A box that takes children and has none draws the builder's invitation instead: empty, it would
 * collapse to nothing, and a merchant cannot point at, drop into, or get back out of a box with no
 * height. A shopper gets nothing either way — the slot draws itself only on a builder address.
 */
function childrenOf(node: ElementNodeDto, path: readonly string[]): React.ReactNode {
  if (node.children.length === 0) {
    return isVoidElement(node.type) ? null : <ElementEmptySlot parentId={node.id} />;
  }
  const next = [...path, node.id];
  return node.children.map((child) => <ElementNodeView key={child.id} node={child} path={next} />);
}

// Draws one node: its own component around the children beneath it.
export const ElementNodeView: React.FC<ElementNodeViewProps> = ({ node, path }) => {
  const context = useElementRender();
  const bound = useBoundNode(node);
  if (bound === null || path.length >= MAX_ELEMENT_DEPTH || path.includes(node.id)) return null;
  const Component = context.renderers[node.type] ?? ELEMENT_RENDERERS[node.type];
  if (!Component) return null;
  return <Component node={bound}>{childrenOf(node, path)}</Component>;
};
